import React, { useEffect, useState } from "react";
import { api, formatINR } from "../lib/api";
import { PageHeader, LoadingRows, KPICard } from "../components/common";
import { Card } from "../components/ui/card";
import {
  Building2, Store, Battery, PackageSearch, HardHat, Wrench, ShieldAlert, CreditCard,
  IndianRupee, TrendingUp, PiggyBank, Receipt, Percent, Activity, Gauge, Boxes,
} from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const HEALTH_COLORS = { excellent: "#10B981", good: "#3B82F6", fair: "#F59E0B", poor: "#EF4444" };

export default function ControlTower() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/control-tower").then((r) => setData(r.data)); }, []);
  if (!data) return <LoadingRows rows={6} />;

  const b = data.business, f = data.financial, p = data.portfolio, o = data.operational;
  return (
    <>
      <PageHeader title="Control Tower" description="Real-time business command center for the RushGro platform." />

      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Business</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard label="Active Brands" value={b.active_brands} icon={Building2} accent="blue" />
          <KPICard label="Active Outlets" value={b.active_outlets} icon={Store} accent="emerald" />
          <KPICard label="Active Assets" value={b.active_assets} icon={Battery} accent="fuchsia" />
          <KPICard label="Available Inventory" value={b.available_inventory} icon={PackageSearch} accent="slate" />
          <KPICard label="Under Installation" value={b.assets_under_installation} icon={HardHat} accent="amber" />
          <KPICard label="Under Maintenance" value={b.assets_under_maintenance} icon={Wrench} accent="amber" />
          <KPICard label="Warranty Claims" value={b.warranty_claims} icon={ShieldAlert} accent="rose" />
          <KPICard label="Active Subscriptions" value={b.active_subscriptions} icon={CreditCard} accent="blue" />
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Financial</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <KPICard label="MRR" value={formatINR(f.mrr)} icon={IndianRupee} accent="emerald" />
          <KPICard label="ARR" value={formatINR(f.arr)} icon={TrendingUp} accent="emerald" />
          <KPICard label="Outstanding" value={formatINR(f.outstanding)} icon={PiggyBank} accent="rose" />
          <KPICard label="Invoice Collected" value={formatINR(f.invoice_collected)} icon={Receipt} accent="emerald" />
          <KPICard label="GST Collected" value={formatINR(f.gst_collected)} icon={Percent} accent="blue" />
          <KPICard label="Collection Efficiency" value={`${f.collection_efficiency}%`} icon={Activity} accent="emerald" />
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 rounded-xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Asset Portfolio</div>
          <div className="grid grid-cols-2 gap-3">
            <KPICard label="Total AUM" value={p.total_aum} icon={Boxes} accent="blue" />
            <KPICard label="Active" value={p.active} icon={Battery} accent="emerald" />
            <KPICard label="Idle" value={p.idle} icon={PackageSearch} accent="slate" />
            <KPICard label="Recovered" value={p.recovered} icon={HardHat} accent="amber" />
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={p.health_distribution} dataKey="count" nameKey="band" outerRadius={70} label={(e) => `${e.band}: ${e.count}`}>
                  {p.health_distribution.map((d, i) => <Cell key={i} fill={HEALTH_COLORS[d.band] || "#94a3b8"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Operational KPIs</div>
          <div className="grid grid-cols-2 gap-3">
            <KPICard label="Installation SLA" value={`${o.installation_sla_percent}%`} icon={Gauge} accent="emerald" />
            <KPICard label="Maintenance SLA" value={`${o.maintenance_sla_percent}%`} icon={Gauge} accent="emerald" />
            <KPICard label="Warranty Res. (days)" value={o.warranty_resolution_days_avg} icon={Activity} accent="blue" />
            <KPICard label="Asset Utilization" value={`${o.asset_utilization_percent}%`} icon={Percent} accent="fuchsia" />
            <KPICard label="Avg Battery Health" value={`${o.avg_battery_health}%`} icon={Battery} accent="emerald" />
            <KPICard label="Avg Backup (hrs)" value={o.avg_backup_hours} icon={Activity} accent="amber" />
          </div>
        </Card>
      </section>
    </>
  );
}
